import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';
import { ABSOLUTE_MAX_SPEED_PROFILE } from '@sb/routine-runner';
import type { StepKind } from '@sb/routine-runner';

import { getPermittedChildStepKindsShared } from '../../step-rules/moveArmToV2';
import { type ValidationRule, type ValidationContext } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { validateMotionSpeed } from '../../util/validators';

import { CanonicalStepRuleBase, type CanonicalValidateArgs } from './base';

class MoveArmToV2Rule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.MoveArmToV2', 'MoveArmToV2');
  }

  protected validate({ children, args, ctx }: CanonicalValidateArgs): void {
    const permittedSteps = this.getPermittedChildSteps(args, ctx);
    this.checkChildrenKinds(children, permittedSteps);
    this.checkConstantSpeed(args);
    this.checkMotionSpeed(args);
  }

  /**
   * Surface legacy `customLimits` percent-based fields as a deprecation
   * prompt.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(args: CanonicalValidateArgs['args']): void {
    if (args.argumentKind !== 'MoveArmToV2') return;
    validateMotionSpeed('Move Arm', args.motionSpeed);
  }

  /**
   * Constant-speed moves require an explicitly-selected tooltip and blending
   * between segments, and cannot exceed the step's default tooltip speed limit.
   *
   * @throws {ActionRequiredError}
   */
  private checkConstantSpeed(args: CanonicalValidateArgs['args']): void {
    if (args.argumentKind !== 'MoveArmToV2') return;
    if (!args.enforceConstantSpeed) return;

    // A constant-speed move requires an explicitly-chosen tooltip: the runner
    // forces it onto every waypoint and fails loud at run time without one
    // (MoveArmToV2 Step.ts rejects a null/'auto' constantSpeedTCPOption).
    // Use 'invalidConfiguration' (not 'missingConfiguration', which maps to a
    // non-blocking warning) so precheckForExecution actually blocks playback —
    // otherwise isRoutinePlayable would still let the routine start and only
    // fail mid-run.
    const tcpOption = args.constantSpeedTCPOption;

    if (tcpOption == null || tcpOption === 'auto') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        fieldId: 'constantSpeed',
        message: 'Select a tooltip for the constant speed motion.',
      });
    }

    // Constant speed can only hold a steady tooltip speed through corners if the
    // move blends between segments, so blending cannot be disabled/None while
    // it is enabled. Use 'invalidConfiguration' (blocking error) so
    // precheckForExecution / isRoutinePlayable actually block playback.
    if (!args.blendConfig?.radius) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        fieldId: 'blending',
        message:
          'Constant speed requires blending. Set a blend radius greater than 0.',
      });
    }

    if (!args.constantSpeedValue) return;

    // The step's effective tooltip speed limit for whichever speed-limit option
    // is selected — a custom value, a percent of robot max, or robot max (no
    // limit). Mirrors the step config's toRoutineRunner (and its edit-time
    // getConstantSpeedActionRequired), which derive the speed profile from
    // motionSpeed over ABSOLUTE_MAX_SPEED_PROFILE, so playback is gated for the
    // percent option (e.g. 1% of max), not just the fully-custom one —
    // otherwise the step-settings UI would show an error the play-time gate
    // (precheckForExecution / isRoutinePlayable) doesn't enforce.
    const { maxTooltipSpeed: tcpLimit } =
      calculateSpeedProfileFromStepMotionSpeed({
        motionSpeed: args.motionSpeed,
        baseSpeedProfile: ABSOLUTE_MAX_SPEED_PROFILE,
      });

    if (tcpLimit == null) return;
    if (args.constantSpeedValue <= tcpLimit) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      fieldId: 'constantSpeed',
      message:
        'Constant speed exceeds the default tooltip speed limit. Reduce the constant speed or increase the tooltip speed limit.',
    });
  }

  /**
   * Get the permitted child steps.
   *
   * The step's own arguments are part of the answer: a constant-speed move
   * accepts Arc children whether or not the `arcStepInMove` flag is on.
   *
   * @param args - The step's arguments.
   * @param ctx - The context.
   * @returns The permitted child steps.
   */
  private getPermittedChildSteps(
    args: CanonicalValidateArgs['args'],
    ctx: ValidationContext,
  ): readonly StepKind[] {
    return getPermittedChildStepKindsShared(
      ctx.featureFlags ?? {},
      args.argumentKind === 'MoveArmToV2' ? args : undefined,
    );
  }

  /**
   * Check if the children kinds are valid.
   * @param children - The children.
   * @param permittedSteps - The permitted steps.
   * @throws {ActionRequiredError}
   */
  private checkChildrenKinds(
    children: Array<{ stepKind: StepKind | string; name?: string }>,
    permittedSteps: readonly StepKind[],
  ): void {
    const allOk = children.every((c) =>
      permittedSteps.includes(c.stepKind as StepKind),
    );

    if (allOk) return;

    const badStep = children.find(
      (c) => !permittedSteps.includes(c.stepKind as StepKind),
    );

    if (!badStep) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Move Arm steps must contain only valid child steps.',
      });
    }

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Move Arm steps may not contain ${badStep.stepKind} steps.`,
    });
  }
}

export const MoveArmToV2Validation: ValidationRule =
  new MoveArmToV2Rule().toValidationRule();
